import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import i18n from 'i18n-js';
import DashedLine from 'react-native-dashed-line';
import moment from 'moment';

import { scale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import { isPhone, localTime } from '@helpers';

import { font } from '@styles/fonts';

import Footer from './footer';
import Hatch from './hatch';

import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { DATE_FORMAT } from '@v2/pages/common/constants/base.constants';
import { getValuesBar, getBarLegend } from '@components/machines/components/eventsChart/calculation';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';

const Chart = ({
  C,
  data: { dataBar = [], start },
  data,
  setData,
  selectedChartDate,
  isDay,
  selectedIndex,
  setSelectedIndex,
  selectedItem,
  setSelectedItem,
  isActive,
  fullWidth,
  getNcFileStatsHour,
  getNcFileStatsDay,
  getNcFileStatsMonth,
  id,
  setNcFiles,
  sendLog,
  allSelected,
}) => {
  const { timePeriod: selectedTime } = useHandleParams();

  if (allSelected) {
    id = '';
  }

  const { legend = [], legendRules = [] } = getBarLegend(data.selectedTime, start);
  const loaded = data?.dataBar?.length;
  
  useEffect(() => {
    (async () => {
      let momentStart = localTime();
      let momentEnd = localTime();
      if (selectedChartDate) {
        momentStart = localTime(selectedChartDate);
        momentEnd = localTime(selectedChartDate);
      }

      setNcFiles([]);

      const performanceStorage = {};

      switch (selectedTime) {
        case 'day': {
          const start = momentStart.clone().startOf('day');
          const end = momentStart.clone().add(1, 'day').startOf('day');
          let t1 = performance.now();
          const data = await getNcFileStatsHour({ id, start: start.format(DATE_FORMAT), end: end.format(DATE_FORMAT) });
          let t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;

          t1 = performance.now();
          const info = getValuesBar({ start: start.format(), data, amount: 24, period: 'hour' });
          t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;
          performanceStorage.firstAmount = data.length;

          setData({
            ...info,
            selectedTime,
            start: start.format(DATE_FORMAT),
            end: end.format(DATE_FORMAT),
          });

          break;
        }
        case 'week': {
          const start = momentStart.clone().startOf('isoWeek');
          const end = momentEnd.clone().add(1, 'isoWeek').startOf('isoWeek');
          let t1 = performance.now();
          const data = await getNcFileStatsDay({ id, start: start.format(DATE_FORMAT), end: end.format(DATE_FORMAT) });
          let t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;

          t1 = performance.now();
          const info = getValuesBar({ start: start.format(), data, amount: 7, period: 'day' });
          t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;
          performanceStorage.firstAmount = data.length;

          setData({
            ...info,
            selectedTime,
            start: start.format(DATE_FORMAT),
            end: end.format(DATE_FORMAT),
          });
          break;
        }
        case 'month': {
          const start = momentStart.clone().startOf('month');
          const end = momentEnd.clone().add(1, 'month').startOf('month');
          let t1 = performance.now();
          const data = await getNcFileStatsDay({ id, start: start.format(DATE_FORMAT), end: end.format(DATE_FORMAT) });
          let t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;

          t1 = performance.now();
          const amount = localTime(momentStart).daysInMonth();
          const info = getValuesBar({ start: start.format(), data, amount, period: 'day' });
          t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;
          performanceStorage.firstAmount = data.length;
          setData({
            ...info,
            selectedTime,
            start: start.format(DATE_FORMAT),
            end: end.format(DATE_FORMAT),
          });
          break;
        }
        case 'year': {
          const start = momentStart.clone().startOf('year');
          const end = momentEnd.clone().add(1, 'year').startOf('year');
          let t1 = performance.now();
          const data = await getNcFileStatsMonth({ id, start: start.format(DATE_FORMAT), end: end.format(DATE_FORMAT) });
          let t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;

          t1 = performance.now();
          const info = getValuesBar({ start: start.format(), data, amount: 12, period: 'month' });
          t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;
          performanceStorage.firstAmount = data.length;

          setData({
            ...info,
            selectedTime,
            start: start.format(DATE_FORMAT),
            end: end.format(DATE_FORMAT),
          });
          break;
        }
      }
      sendLog({ title: LOGGER_TITLES.BAR_CHART_PERFORMANCE, data: { ...performanceStorage, selectedTime, machine: id }, status: 'debug' });
    })();
  }, [selectedTime, id, selectedChartDate]);

  if (!loaded) {
    return (
      <View style={{ marginTop: scale(70) }}>
        <SmallLoading />
      </View>
    );
  }

  const hourInSeconds = 60 * 60;

  const chartWidth = isPhone ? fullWidth - scale(60, false) : scale(384, false);
  const chartHeight = scale(119, false);

  const barBlockWidth = chartWidth / (dataBar.length || 1);
  const barWidth = barBlockWidth * 0.66;

  const heightValues = isActive ? ['active'] : ['setup', 'change', 'active'];

  let formatedData = dataBar.map((item) => ({
    ...item,
    total: heightValues.reduce((a, key) => {
      return item[key] + a;
    }, 0),
  }));

  const maxValue = Math.max(...formatedData.map((i) => i.total));

  const rightLegend = (() => {
    const hours = Math.ceil(maxValue / hourInSeconds);
    if (hours <= 1 && isDay) return [0, 0.5, 1];
    if (hours <= 2) return [0, 1, 2];
    if (hours <= 4) return [0, 2, 4];
    if (hours <= 6) return [0, 3, 6];
    if (hours <= 12) return [0, 6, 12];
    if (hours <= 18) return [0, 9, 18];
    if (hours <= 24) return [0, 12, 24];

    return [0, Math.round(hours / 2), hours];
  })();

  const topPoint = rightLegend[2] * hourInSeconds;

  formatedData = formatedData.map((item) => {
    const height = {};
    heightValues.forEach((key) => {
      height[key + 'Height'] = (item[key] * 100) / topPoint;
    });
    return {
      ...item,
      height: (item.total * 100) / topPoint,
      ...height,
    };
  });

  const rightLegendFontOffset = scale(10, false);

  const legendLength = legend.length || 1;
  const bottomLegendBlockWidth = chartWidth / legendLength;

  const rightFooter = selectedItem
    ? { active: selectedItem.active, setup: selectedItem.setup, change: selectedItem.change }
    : { active: data.totalActive, setup: data.totalSetup, change: data.totalChange };

  const formatLegend = (v) => (isDay ? `${v * 60}${i18n.t('m')}` : `${v}${i18n.t('h')}`);

  return (
    <View
      style={{
        height: scale(128),
        marginTop: scale(4),
        width: chartWidth,
        borderColor: C('Hairline/Light/Secondary'),
        borderLeftWidth: StyleSheet.hairlineWidth,
        borderTopWidth: StyleSheet.hairlineWidth,
      }}
    >
      <View
        style={{
          height: chartHeight,
          width: chartWidth,
          borderColor: C('Hairline/Light/Secondary'),
          borderBottomWidth: StyleSheet.hairlineWidth,
        }}
      >
        <View
          style={{
            height: StyleSheet.hairlineWidth,
            width: '100%',
            backgroundColor: C('Hairline/Light/Secondary'),
            position: 'absolute',
            top: chartHeight / 2,
          }}
        />
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'flex-end',
            height: '100%',
          }}
        >
          {formatedData.map((item, index) => {
            const calc = (i) => (i > 0 ? 1 : 0);

            const render = [];
            const colors = {
              change: C('Status/Light/Change'),
              active: C('Status/Light/Active'),
              setup: C('Status/Light/Setup'),
            };
            const colorsHatch = {
              change: C('Status/Light/Change'),
              active: C('Status/Light/Active'),
              setup: C('Status/Light/Setup'),
            };
            const hatchKey = [];

            heightValues.forEach((key) => {
              const v = item[key + 'Height'];
              render.push({
                min: calc(v),
                height: `${(v * 100) / item.height}%`,
                color: colors[key],
                showHatch: hatchKey.includes(key),
                colorHatch: colorsHatch[key],
                key,
              });
            });

            const minHeight = Object.values(render).reduce((a, i) => i.min + a, 0);
            const isSelected = selectedIndex === index;
            return (
              <TouchableElement
                key={`bar-${index}`}
                hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                onPress={() => {
                  if (isSelected) {
                    setSelectedIndex(null);
                    setSelectedItem(null);
                  } else {
                    setSelectedIndex(index);
                    item.height = Math.max(minHeight, item.height);
                    setSelectedItem(item);
                  }
                }}
              >
                {({ boxStyle }) => {
                  return (
                    <View
                      style={[
                        boxStyle,
                        {
                          height: '100%',
                          flexDirection: 'row',
                          alignItems: 'flex-end',
                          width: barBlockWidth,
                          position: 'absolute',
                          zIndex: 1000,
                          left: index * bottomLegendBlockWidth,
                          opacity: selectedIndex !== null && !isSelected ? 0.5 : 1,
                        },
                      ]}
                    >
                      <View
                        style={[
                          {
                            width: barBlockWidth,
                            height: `${item.height}%`,
                            minHeight: minHeight,
                            alignItems: 'center',
                          },
                        ]}
                      >
                        <View
                          style={[
                            boxStyle,
                            {
                              width: barWidth,
                              height: '100%',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              borderTopLeftRadius: scale(2),
                              borderTopRightRadius: scale(2),
                              overflow: 'hidden',
                              backgroundColor: C('Background/Light/System'),
                            },
                          ]}
                        >
                          {render.map((item) => (
                            <View
                              key={item.key}
                              style={{
                                width: barWidth,
                                height: item.height,
                                backgroundColor: item.color,
                                minHeight: item.min,
                                overflow: 'hidden',
                              }}
                            >
                              {item.showHatch && <Hatch {...{ color: item.colorHatch }} />}
                            </View>
                          ))}
                        </View>
                      </View>
                    </View>
                  );
                }}
              </TouchableElement>
            );
          })}
          {legend.map((item, index) => (
            <View
              key={`legend-${index}`}
              style={{
                position: 'absolute',
                top: 0,
                width: bottomLegendBlockWidth,
                left: index * bottomLegendBlockWidth,
                height: scale(136),
              }}
            >
              {legendRules.includes(item) ? (
                <>
                  <View
                    style={{
                      position: 'absolute',
                      width: StyleSheet.hairlineWidth,
                      height: '100%',
                      left: -StyleSheet.hairlineWidth / 2,
                      opacity: index,
                    }}
                  >
                    <DashedLine
                      axis="vertical"
                      style={{ height: scale(136) }}
                      dashThickness={StyleSheet.hairlineWidth}
                      dashLength={scale(2)}
                      dashGap={scale(2)}
                      dashColor={C('Label/Light/Quaternary')}
                    />
                  </View>
                  <Text
                    numberOfLines={1}
                    style={[
                      font.Footnote,
                      {
                        color: C('Label/Light/Quaternary'),
                        position: 'absolute',
                        bottom: scale(-3),
                        left: (barBlockWidth - barWidth) / 2,
                        minWidth: scale(20),
                      },
                    ]}
                  >
                    {i18n.t(String(item))}
                  </Text>
                </>
              ) : null}
              {index === legend.length - 1 && (
                <View
                  style={{
                    position: 'absolute',
                    width: StyleSheet.hairlineWidth,
                    height: '100%',
                    right: 0,
                  }}
                >
                  <DashedLine
                    axis="vertical"
                    style={{ height: scale(136) }}
                    dashThickness={StyleSheet.hairlineWidth}
                    dashLength={scale(2)}
                    dashGap={scale(2)}
                    dashColor={C('Label/Light/Quaternary')}
                  />
                </View>
              )}
            </View>
          ))}
        </View>
        <View
          style={{
            position: 'absolute',
            top: 0,
            right: scale(-40),
            height: '100%',
            width: scale(40),
          }}
        >
          <View style={{ position: 'absolute', top: -rightLegendFontOffset, left: scale(6) }}>
            <Text style={[font['Footnote'], { color: C('Label/Light/Quaternary') }]}>{formatLegend(rightLegend[2])}</Text>
          </View>
          <View style={{ position: 'absolute', top: chartHeight / 2 - rightLegendFontOffset, left: scale(6) }}>
            <Text style={[font['Footnote'], { color: C('Label/Light/Quaternary') }]}>{formatLegend(rightLegend[1])}</Text>
          </View>
          <View style={{ position: 'absolute', bottom: -rightLegendFontOffset, left: scale(6) }}>
            <Text style={[font['Footnote'], { color: C('Label/Light/Quaternary') }]}>{formatLegend(rightLegend[0])}</Text>
          </View>
        </View>
      </View>
      {!isActive && <Footer {...{ ...rightFooter, C, isDay, selectedIndex, loaded }} />}
    </View>
  );
};

export default Chart;
